import { Loader2, Paperclip, UploadCloud } from "lucide-react";
import React, { useCallback, useLayoutEffect, useState } from "react";
import { FileRejection, useDropzone, type Accept } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { cn } from "@helpers/utils";
import BaseInput from "@reusables/BaseInput";
import BaseMaterialButton from "@reusables/BaseMaterialButton";
import BaseTooltip from "@reusables/BaseTooltip";

import FileRow, { ReceiptDocumentItem } from "./FileRow";

const MAX_RECEIPT_DOCUMENT_SIZE_MB = 5;
const MAX_RECEIPT_DOCUMENT_SIZE = MAX_RECEIPT_DOCUMENT_SIZE_MB * 1024 * 1024;

const RECEIPT_DOCUMENT_ACCEPT: Accept = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
};

type PanelProps = {
    documentNumberValue?: string | null;
    documentNumberDisabled?: boolean;
    documentNumberError?: React.ComponentProps<typeof BaseInput>["error"];
    files: ReceiptDocumentItem[];
    uploadDisabled?: boolean;
    uploadDisabledReason?: string;
    uploadLoading?: boolean;
    deleteDisabled?: boolean;
    deleteDisabledReason?: string;
    downloadDisabled?: boolean;
    documentNumberAction?: JSX.Element;
    onDocumentNumberChange?: (value: string) => void;
    onFilesAccepted: (files: File[]) => void;
    onDownload: (file: ReceiptDocumentItem) => void;
    onDelete?: (file: ReceiptDocumentItem) => void;
};

export default function Panel({
    documentNumberValue,
    documentNumberDisabled,
    documentNumberError,
    files,
    uploadDisabled,
    uploadDisabledReason,
    uploadLoading,
    deleteDisabled,
    deleteDisabledReason,
    downloadDisabled,
    documentNumberAction,
    onDocumentNumberChange,
    onFilesAccepted,
    onDownload,
    onDelete,
}: PanelProps) {
    const { t } = useTranslation("", { keyPrefix: "receive" });

    const onDropRejected = useCallback(
        (fileRejections: FileRejection[]) => {
            const firstRejection = fileRejections[0];
            const firstError = firstRejection?.errors[0];

            if (!firstRejection || !firstError) return;

            if (firstError.code === "file-too-large") {
                toast.error(
                    t("documents.validation.tooLarge", {
                        file: firstRejection.file.name,
                        maxSize: MAX_RECEIPT_DOCUMENT_SIZE_MB,
                    })
                );
                return;
            }

            if (firstError.code === "file-invalid-type") {
                toast.error(
                    t("documents.validation.invalidType", {
                        file: firstRejection.file.name,
                    })
                );
                return;
            }

            toast.error(
                t("documents.validation.rejected", {
                    file: firstRejection.file.name,
                })
            );
        },
        [t]
    );

    const { getInputProps, getRootProps, isDragActive, open, rootRef } = useDropzone({
        accept: RECEIPT_DOCUMENT_ACCEPT,
        maxSize: MAX_RECEIPT_DOCUMENT_SIZE,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        disabled: uploadDisabled || uploadLoading,
        onDropAccepted: onFilesAccepted,
        onDropRejected,
    });
    const height = useMeasuredElementHeight(rootRef as React.RefObject<HTMLDivElement>);

    const uploadButton = (
        <BaseMaterialButton
            type="button"
            size="medium"
            outlined
            fontSize={14}
            fontWeight={500}
            padding="0 16px"
            className="gap-[8px]"
            sx={{ height: 57, minWidth: 128 }}
            disabled={uploadDisabled || uploadLoading}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                open();
            }}
        >
            {uploadLoading ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : (
                <Paperclip size={16} aria-hidden="true" />
            )}
            <span>{t("documents.upload.button")}</span>
        </BaseMaterialButton>
    );

    return (
        <div className="t-resize overflow-hidden rounded-[8px]" style={{ height }}>
            <div
                {...getRootProps({
                    className: cn(
                        "relative rounded-[8px] border border-solid border-gray-300 p-[16px] transition-[background-color,border-color] duration-200",
                        isDragActive && "border-dashed border-[#7556FA] bg-[#F7F5FF]"
                    ),
                })}
            >
                <input {...getInputProps()} />

                <div className="flex flex-col gap-[16px] md:flex-row md:items-end md:justify-between">
                    <div className="min-w-[260px] flex-1">
                        <BaseInput
                            label={t("documents.fields.documentNumber.label")}
                            placeholder={t("documents.fields.documentNumber.placeholder")}
                            value={documentNumberValue ?? ""}
                            onChange={(value) => onDocumentNumberChange?.(`${value}`)}
                            disabled={documentNumberDisabled}
                            error={documentNumberError}
                            icon={
                                documentNumberAction
                                    ? {
                                          right: {
                                              el: documentNumberAction,
                                              offset: 8,
                                          },
                                      }
                                    : undefined
                            }
                            sx={{
                                ".MuiInput-input": {
                                    paddingRight: documentNumberAction ? "48px" : undefined,
                                },
                            }}
                        />
                    </div>

                    <div className="flex shrink-0 justify-end">
                        <BaseTooltip
                            active={!!uploadDisabled && !!uploadDisabledReason}
                            title={uploadDisabledReason ?? ""}
                            placement="top"
                        >
                            {uploadButton}
                        </BaseTooltip>
                    </div>
                </div>

                <div className="mt-[8px] text-xs text-gray-500">
                    {t("documents.upload.helper", { maxSize: MAX_RECEIPT_DOCUMENT_SIZE_MB })}
                </div>

                {files.length > 0 && (
                    <div className="mt-[16px] space-y-[8px]">
                        {files.map((file) => (
                            <FileRow
                                key={file.key}
                                file={file}
                                deleteDisabled={deleteDisabled}
                                deleteDisabledReason={deleteDisabledReason}
                                downloadDisabled={downloadDisabled}
                                onDownload={onDownload}
                                onDelete={onDelete}
                            />
                        ))}
                    </div>
                )}

                {isDragActive && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[8px] bg-white/80">
                        <div className="flex items-center gap-[10px] rounded-[8px] border border-dashed border-[#7556FA] bg-white px-[18px] py-[12px] text-accent shadow-[0_8px_24px_rgba(60,55,105,0.12)]">
                            <UploadCloud size={20} />
                            <span className="font-medium">{t("documents.upload.overlay")}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function useMeasuredElementHeight<T extends HTMLElement>(contentRef: React.RefObject<T>) {
    const [height, setHeight] = useState<number>();

    useLayoutEffect(() => {
        const element = contentRef.current;

        if (!element) return;

        const updateHeight = () => {
            setHeight(element.offsetHeight);
        };

        updateHeight();

        const observer = new ResizeObserver(updateHeight);
        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, []);

    return height ? `${height}px` : undefined;
}
